'use client';

import classNames from 'classnames';
import { useParams, useSearchParams } from 'next/navigation';
import { overlay } from 'overlay-kit';
import { Suspense, useEffect, useRef, useState } from 'react';

import { realtorMemulListQuery } from '@/app/(realtor)/realtor/[realtorCd]/api';
import { RealtorMemulListParamsType } from '@/app/(realtor)/realtor/[realtorCd]/type';
import RealtorDetailBottom from '@/app/(realtor)/realtor/[realtorCd]/ui/bottom';
import BottomSheet from '@/components/common/bottomSheets';
import Sort from '@/components/common/bottomSheets/sort';
import MemulItem from '@/components/common/memulItem';
import NoData from '@/components/common/noData';
import ScrollGradient from '@/components/memul/list/scrollGradient';
import SkeletonMemulList from '@/components/memul/list/skeleton/skeletonMemul';
import { BUSINESS_DIV_TYPE, BusinessDivKey } from '@/const/buildingType';
import { SORT_LIST, SortListTypeKey } from '@/const/sort';
import { MemulItemType } from '@/service/commonType';
import useMobileStore from '@/store/mobileStore';
import useInfinityHook from '@/utils/hooks/useInfinityHook';
import useRemoveOneSearchParam from '@/utils/hooks/useRemoveOneSearchParam';
import useScrollDirection from '@/utils/hooks/useScrollDirection';
import { useShallowRouting } from '@/utils/hooks/useShallowRouting';
import { objectEntryCodeNTitle } from '@/utils/js/condition';
import { formatWithCommas } from '@/utils/js/number';

function RealtorMemulListWrapper({
  setDataLength,
  params,
}: {
  setDataLength: (number: number) => void;
  params: RealtorMemulListParamsType;
}) {
  const { dataArray, ref, hasNextPage, isFetchingNextPage } = useInfinityHook<MemulItemType>({
    queryFn: realtorMemulListQuery(params),
    setDataLength,
    isSuspense: true,
  });

  if (!hasNextPage && dataArray?.length === 0)
    return (
      <NoData
        title={'등록된 매물이 없어요'}
        sub={'중개사무소에 문의하고 원하는 조건의 매물을 소개받아 보세요'}
      />
    );

  return (
    <>
      {dataArray?.map((item: MemulItemType, index) => (
        <MemulItem key={item?.articleCd + index} itemData={item} page={'realtor'} />
      ))}
      <div ref={ref}>{isFetchingNextPage && <SkeletonMemulList />}</div>
    </>
  );
}

export default function RealtorMemulList() {
  const [dataLength, setDataLength] = useState(0);
  const { realtorCd } = useParams();
  const { params } = useShallowRouting();
  const [selectSort, setSelectSort] = useState<string>(
    (params.sort as string) ?? Object.keys(SORT_LIST)[0],
  );
  const [businessDiv, setBusinessDiv] = useState<BusinessDivKey>();
  const searchParams = useSearchParams();
  const businessDivParam = searchParams.get('businessDiv');
  const removeOneSearchParam = useRemoveOneSearchParam();
  const gradientStandardRef = useRef<HTMLDivElement>(null);
  const scrollDirection = useScrollDirection();
  const { isMobile } = useMobileStore();

  const handleSortClick = () => {
    overlay.open(({ isOpen, close }) => {
      return (
        <BottomSheet isOpen={isOpen} close={close} title="정렬">
          <Sort
            close={close}
            sort={selectSort}
            setState={setSelectSort}
            dataObj={objectEntryCodeNTitle(SORT_LIST)}
          />
        </BottomSheet>
      );
    });
  };

  useEffect(() => {
    if (businessDivParam) {
      setBusinessDiv(businessDivParam as BusinessDivKey);
      removeOneSearchParam('businessDiv');
    }
  }, [businessDivParam]);

  return (
    <div className="relator_wrap">
      <div className="relator_memul_list_wrap">
        <div
          className={classNames('page_tab_list', { sticky: scrollDirection === 'up' })}
          style={{ top: '5.6rem' }}
        >
          <button
            className={classNames('page_tab', { active: businessDiv === undefined })}
            onClick={() => setBusinessDiv(undefined)}
          >
            전체
          </button>
          {objectEntryCodeNTitle(BUSINESS_DIV_TYPE).map((item) => (
            <button
              key={`businessDiv${item.code}`}
              onClick={() => setBusinessDiv(item.code as BusinessDivKey)}
              className={classNames('page_tab', { active: businessDiv === item.code })}
            >
              {item.title}
            </button>
          ))}
        </div>
        <div className="memul_list_wrap" ref={gradientStandardRef}>
          <ScrollGradient ref={gradientStandardRef} />
          <div
            className={classNames('sorting_list_top', { sticky: scrollDirection === 'up' })}
            style={{ top: '10rem' }}
          >
            <div className="count_div">
              <p>전체</p>
              <p className="count">{formatWithCommas(dataLength)}</p>
            </div>
            <button className="select_btn" onClick={handleSortClick}>
              {SORT_LIST[selectSort as SortListTypeKey]}
            </button>
          </div>

          <Suspense fallback={<SkeletonMemulList />}>
            <RealtorMemulListWrapper
              setDataLength={setDataLength}
              params={{
                agencyNo: realtorCd as string,
                sort: selectSort as SortListTypeKey,
                businessDiv,
              }}
            />
          </Suspense>
        </div>
      </div>
      {isMobile && <RealtorDetailBottom />}
    </div>
  );
}
